import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiResponse, SiteLocation } from 'app/shared/types';
import { environment } from 'environment/environment';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api.service';
import { CONTROLLERS } from './controllers';
import { SITE_ID } from './tokens/tokens.const';

@Injectable({
  providedIn: 'root',
})
export class LocationsService extends BaseApiService<
  ApiResponse<{ locations: SiteLocation[] }>,
  ApiResponse<{ location: SiteLocation }>,
  ApiResponse<{ id: string }>
> {
  constructor(
    public override http: HttpClient,
    @Inject(SITE_ID) private siteId: string,
  ) {
    super(http, environment.siteManagementApiBaseUrl);
  }

  public override controllerName(): string {
    if (!this.siteId) {
      throw new Error('Site ID is not set.');
    }

    return `api/sites/${this.siteId}/locations`;
  }

  public getWithVersion(isLocationsHierarchyEnabled: boolean, locationType?: string ): Observable<ApiResponse<{ locations: SiteLocation[] }>> {
    if (!isLocationsHierarchyEnabled) {
      return super.get();
    }

    let params = new HttpParams().set('api-version', '2.0');

    if (locationType) {
      params = params.append('locationType', locationType);
      }

    return this.http.get<ApiResponse<{ locations: SiteLocation[] }>>(`${this.api}/${this.controllerName()}`, {
      params,
    });
  }

  public insertWithVersion(
    location: SiteLocation,
    isLocationsHierarchyEnabled: boolean,
  ): Observable<ApiResponse<{ id: string }>> {
    if (!isLocationsHierarchyEnabled) {
      return super.insert(location);
    }

    const params = new HttpParams().set('api-version', '2.0');
    const headers = new HttpHeaders().set('X-Lazer-Track-API', `insert/${this.controllerName()}`);

    return this.http.post<ApiResponse<{ id: string }>>(`${this.api}/${this.controllerName()}`, location, {
      headers,
      params,
    });
  }

  public updateWithVersion(
    id: string,
    location: SiteLocation,
    isLocationsHierarchyEnabled: boolean,
  ): Observable<ApiResponse<{ id: string }>> {
    if (!isLocationsHierarchyEnabled) {
      return super.update(id, location);
    }

    const params = new HttpParams().set('api-version', '2.0');
    const headers = new HttpHeaders().set('X-Lazer-Track-API', `update/${this.controllerName()}/${id}`);

    return this.http.put<ApiResponse<{ id: string }>>(`${this.api}/${this.controllerName()}/${id}`, location, {
      headers,
      params,
    });
  }

  public deleteWithVersion(id: string, isLocationsHierarchyEnabled: boolean): Observable<boolean> {
    if (!isLocationsHierarchyEnabled) {
      return super.delete(id);
    }

    const params = new HttpParams().set('api-version', '2.0');
    const headers = new HttpHeaders().set('X-Lazer-Track-API', `delete/${this.controllerName()}/${id}`);

    return this.http.delete<boolean>(`${this.api}/${this.controllerName()}/${id}`, { headers, params });
  }

  public importCsvWithVersion(
    file: File,
    isLocationsHierarchyEnabled: boolean,
  ): Observable<ApiResponse<{ imported: boolean; importedAt: Date }>> {
    const formData = new FormData();
    formData.append('csvFile', file, file.name);

    if (!isLocationsHierarchyEnabled) {
      return this.http.post<ApiResponse<{ imported: boolean; importedAt: Date }>>(
        `${this.api}/${this.controllerName()}/import/csv`,
        formData,
      );
    }
    const params = new HttpParams().set('api-version', '2.0');
    const headers = new HttpHeaders().set('X-Lazer-Track-API', `import/${this.controllerName()}/csv`);

    return this.http.post<ApiResponse<{ imported: boolean; importedAt: Date }>>(
      `${this.api}/${this.controllerName()}/import/csv`,
      formData,
      { headers, params },
    );
  }

  public getFreeDoors(): Observable<ApiResponse<{ locations: SiteLocation[] }>> {
    return this.http.get<ApiResponse<{ locations: SiteLocation[] }>>(
      `${this.api}/${CONTROLLERS.sites}/${this.siteId}/free-doors`,
    );
  }
}
